<script lang="ts">
  import { _ } from 'svelte-i18n';
  import type { Document } from '../api';
  import FileText from 'lucide-svelte/icons/file-text';
  import FileImage from 'lucide-svelte/icons/file-image';
  import FileQuestion from 'lucide-svelte/icons/file-question';
  import { isInProcessing } from '../documentStatus';
  import { Spinner } from './ui/index';
  import SignedThumbnailImage from './SignedThumbnailImage.svelte';

  export let document: Document;
  export let size: 'medium' | 'large' = 'large';
  /** Alt text of the preview; defaults to the file name. */
  export let alt: string | undefined = undefined;
  /** Defer loading of off-screen previews */
  export let lazy = false;
  /** Extra classes for the container */
  export let className = '';

  let loading = true;
  let error = false;

  $: thumbnailKey = `${document.id}|${size}`;
  $: isProcessing = isInProcessing(document);

  // Reset the loading state only when the actual image source changes. Reacting to
  // the whole document object would re-show the spinner on unrelated updates
  // (e.g. assignedTo) without a new <img load> event ever firing (#207).
  let lastThumbnailKey: string | undefined;
  $: if (thumbnailKey !== lastThumbnailKey) {
    lastThumbnailKey = thumbnailKey;
    loading = true;
    error = false;
  }

  function handleLoad() {
    loading = false;
    error = false;
  }

  function handleError() {
    loading = false;
    error = true;
  }
</script>

<div
  class="relative w-full aspect-[3/4] bg-bg-alt border border-border rounded overflow-hidden flex items-center justify-center {className}"
>
  {#if !isProcessing}
    <SignedThumbnailImage
      documentId={document.id}
      {size}
      alt={alt ?? document.fileName}
      {lazy}
      className="w-full h-full object-contain transition-opacity duration-300 {loading || error
        ? 'opacity-0'
        : 'opacity-100'}"
      on:load={handleLoad}
      on:error={handleError}
    />
  {/if}

  {#if (loading && !isProcessing && !error) || isProcessing || error}
    <div class="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
      {#if isProcessing}
        <div class="animate-pulse mb-3">
          <div class="relative">
            <FileText class="w-16 h-16 text-accent/20" strokeWidth={1} />
            <div class="absolute inset-0 flex items-center justify-center">
              <Spinner size="sm" />
            </div>
          </div>
        </div>
        <p class="text-[10px] uppercase tracking-widest text-accent font-semibold animate-pulse">
          {$_('components.document_thumbnail.processing')}
        </p>
      {:else if error}
        <div class="opacity-30 mb-2">
          {#if document.mimeType?.startsWith('image/')}
            <FileImage class="w-16 h-16 text-text-muted" strokeWidth={1} />
          {:else if document.mimeType === 'application/pdf'}
            <FileText class="w-16 h-16 text-text-muted" strokeWidth={1} />
          {:else}
            <FileQuestion class="w-16 h-16 text-text-muted" strokeWidth={1} />
          {/if}
        </div>
        <p class="text-[10px] uppercase tracking-wider text-text-muted font-medium">
          {$_('components.document_thumbnail.no_preview')}
        </p>
      {:else if loading && !error}
        <Spinner size="md" />
      {/if}
    </div>
  {/if}
</div>
